import type { ProductDetails_images } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/ProductDetails";
import useEmblaCarousel from "embla-carousel-react";
import Image, { type ImageLoader } from "next/image";
import React, { useCallback, useEffect, useState } from "react";

import { imageLoaderUri } from "@app/constants";
import { CarouselButtons } from "@components/atoms";
import WishlistButton from "@components/molecules/WishlistButton/WishlistButton";

import { Embla, EmblaDots, EmblaWrapper } from "./styles";

const GalleryCarousel: React.FC<{
  images?: ((ProductDetails_images & { alt?: string }) | null)[] | null;
  handleChange?: (index: number) => void;
  imageFit?: "contain" | "cover";
  toggleWishlist?: () => void;
  isInWishlist?: boolean;
}> = ({
  images,
  handleChange = () => {},
  imageFit = "contain",
  toggleWishlist,
  isInWishlist,
}) => {
  const [viewportRef, embla] = useEmblaCarousel({
    skipSnaps: false,
    loop: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla]
  );

  const handleClickDotButton = (e, index) => {
    e.stopPropagation();
    scrollTo(index);
  };

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    handleChange(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on("select", onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const imageLoader: ImageLoader = ({ src, width, quality }) => {
    const path = new URL(src).pathname;
    return `${imageLoaderUri}/_next/image/?url=${path}&w=${width || 550}&q=${
      quality || 75
    }`;
  };

  return (
    <EmblaWrapper>
      {/* GalleryCarousel is used elsewhere, in places where we dont want to add the wishlist button, 
      so only when it receives the toggleWishlist function will it appear */}
      {!!toggleWishlist && (
        <WishlistButton
          toggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
        />
      )}

      <Embla imageFit={imageFit}>
        <div className="embla__viewport" ref={viewportRef}>
          <div className="embla__container">
            {!!images &&
              images.map((image) => {
                if (!image) {
                  return <></>;
                }
                const { id, url, mobileUrl, alt } = image;
                // const firstImage = index === 0;

                return (
                  <div className="embla__slide" key={id}>
                    <div className="embla__slide__inner">
                      <Image
                        // NOTES img element has fetchPriority prop, but currently on our version of nextjs it is not defined in the types. fetchPriority was added on Next.js 13.3.0 https://github.com/vercel/next.js/discussions/39740#discussioncomment-6853256
                        // @ts-expect-error
                        fetchPriority="high"
                        priority
                        src={mobileUrl || url}
                        loader={imageLoader}
                        layout="fill"
                        alt={alt || `product_#${id}`}
                        objectFit="contain"
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Embla>
      <EmblaDots>
        {scrollSnaps.map((_, index) => (
          <CarouselButtons
            key={index}
            selected={index === selectedIndex}
            onClick={(e) => handleClickDotButton(e, index)}
          />
        ))}
      </EmblaDots>
    </EmblaWrapper>
  );
};

export default GalleryCarousel;
